import { useState, useEffect, useRef } from 'react'
import { Box, Circle, Flex, HStack, Text, useColorModeValue, Icon, Portal } from '@chakra-ui/react'
import { FiChevronDown, FiChevronUp } from 'react-icons/fi'

export function ColorSelector({ value, onChange, options = [], isDisabled = false }) {
  const [selectedColor, setSelectedColor] = useState(value || '')
  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef(null)

  // Atualiza o estado local quando o valor externo muda
  useEffect(() => {
    setSelectedColor(value || '')
  }, [value])

  // Fecha o dropdown quando clica fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Encontra a cor hexadecimal para a cor selecionada
  const getSelectedColorHex = () => {
    const selectedOption = options.find((color) => color.nome_cor === selectedColor)
    return selectedOption?.hexa_cor || '#CCCCCC'
  }

  // Manipula a seleção de uma cor
  const handleColorSelect = (colorName) => {
    if (isDisabled) return
    setSelectedColor(colorName)
    onChange('cor_produto', colorName)
    setIsOpen(false) // Fecha o dropdown após a seleção
  }

  // Alterna a abertura/fechamento do dropdown
  const toggleDropdown = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen)
    }
  }

  // Estilos
  const bgColor = useColorModeValue(isOpen ? 'white' : 'transparent', 'gray.800')
  const borderColor = useColorModeValue(isOpen ? 'blue.500' : 'gray.200', isOpen ? 'blue.300' : 'gray.600')
  const hoverBg = useColorModeValue('gray.50', 'gray.700')

  return (
    <Box ref={selectRef} position="relative" w="full">
      {/* Seletor que parece um select */}
      <Box
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
        bg={bgColor}
        p={'5.5px'}
        w="full"
        opacity={isDisabled ? 0.6 : 1}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        onClick={toggleDropdown}
        _hover={{ bg: isDisabled ? bgColor : hoverBg }}
        transition="all 0.2s"
        boxShadow={isOpen ? 'sm' : 'none'}
      >
        <Flex justify="space-between" align="center">
          <HStack>
            <Circle size="20px" bg={getSelectedColorHex()} border="1px solid" borderColor="gray.300" />
            <Text fontSize="xs" fontWeight="medium">
              {selectedColor || 'Selecione uma cor'}
            </Text>
          </HStack>
          <Icon as={isOpen ? FiChevronUp : FiChevronDown} />
        </Flex>
      </Box>

      {/* Dropdown de opções */}
      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left="0"
          right="0"
          zIndex={10}
          bg={bgColor}
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="md"
          maxH="200px"
          overflowY="auto"
          p={2}
        >
          {options.length === 0 ? (
            <Text fontSize="xs" p={2} textAlign="center" color="gray.500">
              Nenhuma cor disponível
            </Text>
          ) : (
            options.map((color) => (
              <Flex
                key={color.nome_cor}
                p={2}
                align="center"
                _hover={{ bg: hoverBg }}
                cursor="pointer"
                borderRadius="md"
                bg={selectedColor === color.nome_cor ? hoverBg : 'transparent'}
                onClick={() => handleColorSelect(color.nome_cor)}
              >
                <Circle size="24px" bg={color.hexa_cor} border="1px solid" borderColor="gray.300" mr={2} />
                <Text fontSize="xs">{color.nome_cor}</Text>
              </Flex>
            ))
          )}
        </Box>
      )}
    </Box>
  )
}
